import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Switch,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";
import { apiService } from "../services/api";

// Standard conversion needed because PushManager.subscribe() requires the
// VAPID public key as a Uint8Array, not the base64url string the backend
// returns it as.
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

// Settings for the Web Push daily check-in reminder — see
// DailyCheckInReminderScheduler on the backend for what actually decides
// when a reminder fires. This card only handles: getting the browser's
// permission, creating/removing the subscription, and the on/off + time
// preference.
//
// Browser-only. Push notifications don't work in every browser (notably
// limited/newer support in Safari/iOS) — the toggle below detects that
// and explains it rather than silently failing.
export default function ReminderSettingsCard() {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [enabled, setEnabled] = useState(false);
  const [time, setTime] = useState("09:00");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setSupported(false);
      setLoading(false);
      return;
    }
    apiService
      .getMyReminderSettings()
      .then((settings) => {
        setEnabled(settings.remindersEnabled);
        setTime(settings.reminderTime || "09:00");
      })
      .catch(() => {
        // No settings yet is a normal, expected state for a patient who's
        // never touched this — not an error to surface.
      })
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (event) => {
    const turningOn = event.target.checked;
    setWorking(true);
    setMessage(null);

    try {
      if (turningOn) {
        const permissionResult = await Notification.requestPermission();
        setPermission(permissionResult);
        if (permissionResult !== "granted") {
          setMessage({ text: "Reminders need notification permission — please allow it in your browser.", severity: "warning" });
          setWorking(false);
          return;
        }

        const registration = await navigator.serviceWorker.ready;
        const vapidKey = await apiService.getVapidPublicKey();
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });

        const raw = subscription.toJSON();
        await apiService.saveMyPushSubscription({
          endpoint: raw.endpoint,
          p256dh: raw.keys.p256dh,
          auth: raw.keys.auth,
        });

        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        await apiService.updateMyReminderSettings({
          remindersEnabled: true,
          reminderTime: time,
          reminderTimezone: timezone,
        });

        setEnabled(true);
        setMessage({ text: "Daily reminders turned on.", severity: "success" });
      } else {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await apiService.deleteMyPushSubscription(subscription.endpoint);
          await subscription.unsubscribe();
        }
        await apiService.updateMyReminderSettings({ remindersEnabled: false });
        setEnabled(false);
        setMessage({ text: "Daily reminders turned off.", severity: "success" });
      }
    } catch (err) {
      console.error("Failed to update reminder settings:", err);
      setMessage({ text: "Something went wrong updating your reminder settings.", severity: "error" });
    } finally {
      setWorking(false);
    }
  };

  const handleTimeChange = async (e) => {
    const newTime = e.target.value;
    setTime(newTime);
    if (enabled) {
      try {
        await apiService.updateMyReminderSettings({ reminderTime: newTime });
      } catch (err) {
        console.error("Failed to update reminder time:", err);
      }
    }
  };

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Daily Check-In Reminders
        </Typography>

        {!supported ? (
          <Alert severity="info">
            Reminders aren't supported in this browser. Try Chrome, Edge, or Firefox.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" paragraph>
              Get a reminder if you haven't logged today's check-in yet.
            </Typography>

            {message && <Alert severity={message.severity} sx={{ mb: 2 }}>{message.text}</Alert>}

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: enabled ? 2 : 0 }}>
              <Typography variant="body2">Enable reminders</Typography>
              <Switch checked={enabled} onChange={handleToggle} disabled={working} />
            </Box>

            {enabled && (
              <TextField
                type="time"
                label="Reminder time"
                size="small"
                fullWidth
                value={time}
                onChange={handleTimeChange}
                InputLabelProps={{ shrink: true }}
              />
            )}

            {permission === "denied" && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Notifications are blocked for this site in your browser settings — reminders
                won't show even if enabled here until that's changed.
              </Alert>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}